/*
Industry landing page (/industries/:slug). Data-driven off industriesData.js and
rendered in the app's MUI theme so it matches the rest of the marketing site.
Warm, plain-spoken, operator-facing copy (see marketing-voice notes).
*/
import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box, Button, Card, Chip, Container, Grid, Link as MuiLink, Stack, Typography, useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import EngineeringIcon from "@mui/icons-material/Engineering";
import MapIcon from "@mui/icons-material/Map";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PedalBikeIcon from "@mui/icons-material/PedalBike";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import GrassIcon from "@mui/icons-material/Grass";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import useSeo from "utils/useSeo";
import { useEnsurePlans } from "store/functions/entitlements";
import { selectPlanCatalogItems } from "store/reducers/uiReducer";
import { industryJsonLd } from "utils/routeMeta";
import { track, EVENTS } from "utils/analytics.js";
import NotFound from "components/domain/NotFound.js";
import { getIndustry } from "./industriesData.js";
import { buildIndustryPlanNote } from "./industryPlanNote.js";
import RouteSheetCanvas from "./RouteSheetCanvas.jsx";
import FlagshipExampleMap from "./FlagshipExampleMap.jsx";

/*
  The coral accent is #d9662f wherever it is drawn (RouteSheetCanvas, borders).
  As small bold *text* on white it measures 3.6:1, under the 4.5:1 AA floor, so
  the text form is a darker shade of the same hue at 5.4:1. Two values on
  purpose — the drawn accent is judged as a graphic, the label as text.
*/
const ACCENT_TEXT = "#b34a18";

const ICONS = {
  clock: AccessTimeIcon, timer: TimelapseIcon, depot: WarehouseIcon, crew: EngineeringIcon,
  map: MapIcon, calendar: CalendarMonthIcon, cold: AcUnitIcon, box: Inventory2Icon,
  patient: FavoriteBorderIcon, bike: PedalBikeIcon, reload: AutorenewIcon,
  grass: GrassIcon, truck: LocalShippingIcon,
};

const Eyebrow = ({ children, center }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, justifyContent: center ? "center" : "flex-start" }}>
    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: "primary.main" }} />
    <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "primary.extraDark" }}>{children}</Typography>
  </Box>
);

export default function IndustryLandingPage() {
  const { slug } = useParams();
  const industry = getIndustry(slug);
  const theme = useTheme();

  // Same builder as the prerendered shell (scripts/generate-route-html.js), so
  // useSeo() replaces the shell's block with an identical one on a direct load.
  const jsonLd = useMemo(() => (industry ? industryJsonLd(industry) : null), [industry]);

  useSeo({
    title: industry ? industry.seoTitle : "Industries",
    description: industry ? industry.seoDescription : undefined,
    canonicalPath: `/industries/${slug}`,
    jsonLd,
  });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // What this page owes a reader about plans, derived from GET /plans rather
  // than written into the copy. Above the NotFound return, because hooks are.
  useEnsurePlans();
  const planCatalog = useSelector(selectPlanCatalogItems);
  const planNote = useMemo(
    () => buildIndustryPlanNote(planCatalog, industry && industry.professionalCapabilities),
    [planCatalog, industry]
  );

  if (!industry) return <NotFound />;

  const f = industry.flagship;
  const warmPanel = theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f6f4ef";

  return (
    <Box sx={{ backgroundColor: "#ffffff" }}>
      {/* Hero */}
      <Box sx={{ background: "linear-gradient(180deg, rgba(230, 252, 251, 0.45) 0%, #FFFFFF 100%)", pt: { xs: 4, md: 7 }, pb: { xs: 4, md: 7 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Eyebrow>{industry.eyebrow}</Eyebrow>
              <Typography variant="h1" sx={{ fontSize: { xs: "2.1rem", md: "3rem" }, fontWeight: 800, color: "dark.main", lineHeight: 1.1, mt: 2, mb: 2.5 }}>
                {industry.h1}
              </Typography>
              <Typography sx={{ fontSize: "1.15rem", color: "text.secondary", lineHeight: 1.55, maxWidth: 460, mb: 3.5 }}>
                {industry.lede}
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {/* Destination changed 2026-09-14: this button used to open the
                    industry-filtered gallery and now opens the flagship example
                    (the gallery moved to the final CTA). The cta_id is kept on
                    purpose so the CTA dashboard stays one continuous series;
                    read clicks before and after that date as different offers. */}
                <Button
                  component={Link}
                  to={f?.exampleSlug ? `/examples/${f.exampleSlug}` : `/use-cases${industry.useCasesQuery || ""}`}
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "industry_hero_examples", page: `industries/${slug}` })}
                  sx={{
                    backgroundColor: "primary.main",
                    px: 3,
                    py: 1.25,
                    fontSize: "0.98rem",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "primary.extraDark" },
                  }}
                >
                  See a real day planned
                </Button>
                <Button
                  component={Link}
                  to="/model-builder"
                  variant="outlined"
                  onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "industry_hero_model_builder", page: `industries/${slug}` })}
                  sx={{
                    px: 3,
                    py: 1.25,
                    fontSize: "0.98rem",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "8px",
                    borderColor: "divider",
                    color: "dark.main",
                    "&:hover": { borderColor: "primary.main", color: "primary.main" },
                  }}
                >
                  Plan with your own fleet
                </Button>
              </Box>
              {/* Reassurance checklist. Each item must stay true: solves finish in
                  seconds (3–60 s budget), the free Starter plan needs no credit card
                  (the Professional trial does), and uploads take .csv/.xlsx/.xls. */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 2.5 }}
                sx={{ mt: 2.5, mb: 0.5 }}
              >
                {[
                  "Routes in seconds",
                  "Free Starter plan — no card required",
                  "Spreadsheet upload (Excel / CSV)",
                ].map((perk) => (
                  <Stack key={perk} direction="row" spacing={0.75} alignItems="center">
                    <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "primary.main" }} />
                    <Typography variant="body2" sx={{ fontSize: "0.8125rem", color: "text.secondary", fontWeight: 500 }}>
                      {perk}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Box sx={{ mt: 4.5, pt: 3, borderTop: "1px solid", borderColor: "divider", display: "flex", gap: 4, flexWrap: "wrap" }}>
                {industry.telemetry.map((t) => (
                  <Box key={t.l}>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: "dark.main", lineHeight: 1.1 }}>{t.n}</Typography>
                    <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>{t.l}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <RouteSheetCanvas {...industry.heroCanvas} aspect="4 / 3.2" label={`${industry.navLabel} route`} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Problem callout */}
        <Box sx={{ my: { xs: 5, md: 7 }, p: { xs: 3, md: 5 }, backgroundColor: warmPanel, border: "1px solid", borderColor: "divider", borderLeft: "5px solid", borderLeftColor: "primary.main", borderRadius: 3 }}>
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Eyebrow>{industry.problem.eyebrow}</Eyebrow>
              <Typography sx={{ mt: 1.5, fontWeight: 800, fontSize: { xs: "1.35rem", md: "1.7rem" }, lineHeight: 1.2, color: "dark.main" }}>
                {industry.problem.pull[0]}<Box component="span" sx={{ color: "primary.main" }}>{industry.problem.pull[1]}</Box>
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography sx={{ color: "text.secondary", fontSize: "1.05rem", lineHeight: 1.65 }}>{industry.problem.body}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Constraints */}
        <Box sx={{ py: { xs: 4, md: 6 } }}>
          <Box sx={{ maxWidth: 620, mb: 4 }}>
            <Eyebrow>{industry.constraintsHead.eyebrow}</Eyebrow>
            <Typography variant="h2" sx={{ mt: 1.5, fontWeight: 800, fontSize: { xs: "1.6rem", md: "2.1rem" }, color: "dark.main" }}>{industry.constraintsHead.h2}</Typography>
            <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: "1.05rem", lineHeight: 1.6 }}>{industry.constraintsHead.sub}</Typography>
          </Box>
          <Grid container spacing={2}>
            {industry.constraints.map((c) => {
              const Icon = ICONS[c.icon] || CheckIcon;
              return (
                <Grid item xs={12} md={4} key={c.title}>
                  <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, p: 3, transition: "all .18s", "&:hover": { borderColor: "primary.main", transform: "translateY(-2px)" } }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: 2.5, backgroundColor: "primary.extraLight", color: "primary.main", display: "grid", placeItems: "center", mb: 1.5 }}>
                      <Icon sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "dark.main", mb: 0.75 }}>{c.title}</Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: "0.95rem", lineHeight: 1.55 }}>{c.body}</Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {/* One line, under the section where the premium claims are densest.
              Derived, so it shrinks by itself the day free gains one of these. */}
          {planNote && (
            <Typography sx={{ mt: 3, color: "text.secondary", fontSize: "0.9rem", lineHeight: 1.6 }}>
              {planNote}{" "}
              <MuiLink component={Link} to="/pricing" sx={{ color: "primary.main", fontWeight: 600 }}>
                Compare plans
              </MuiLink>
            </Typography>
          )}
        </Box>

        {/* Objective */}
        <Box sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={5} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Eyebrow>{industry.objective.eyebrow}</Eyebrow>
              <Typography sx={{ mt: 1.5, fontWeight: 800, fontSize: { xs: "1.5rem", md: "2rem" }, lineHeight: 1.2, color: "dark.main" }}>
                {industry.objective.big[0]}<Box component="span" sx={{ color: "primary.main" }}>{industry.objective.big[1]}</Box>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
                {industry.objective.points.map((p) => (
                  <Box key={p.b} sx={{ display: "flex", gap: 1.75, alignItems: "flex-start" }}>
                    <Box sx={{ flex: "none", width: 26, height: 26, mt: "2px", borderRadius: "50%", backgroundColor: "primary.extraLight", color: "primary.main", display: "grid", placeItems: "center" }}>
                      <CheckIcon sx={{ fontSize: 15 }} />
                    </Box>
                    <Typography sx={{ fontSize: "1.02rem", color: "text.secondary", lineHeight: 1.55 }}>
                      <Box component="span" sx={{ fontWeight: 800, color: "dark.main" }}>{p.b}</Box>{" "}{p.s}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Proof — real example */}
        <Box sx={{ py: { xs: 4, md: 6 } }}>
          <Box sx={{ maxWidth: 620, mb: 4 }}>
            <Eyebrow>{f.eyebrow}</Eyebrow>
            <Typography variant="h2" sx={{ mt: 1.5, fontWeight: 800, fontSize: { xs: "1.6rem", md: "2.1rem" }, color: "dark.main" }}>{f.h2title}</Typography>
            <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: "1.05rem", lineHeight: 1.6 }}>{f.sub}</Typography>
          </Box>
          <Card variant="outlined" sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 16px 38px -26px rgba(43,55,66,.30)" }}>
            <Grid container>
              <Grid item xs={12} md={6} sx={{ p: 3.5, backgroundColor: warmPanel, borderRight: { md: "1px solid" }, borderBottom: { xs: "1px solid", md: "none" }, borderColor: "divider !important" }}>
                <FlagshipExampleMap slug={f.exampleSlug} canvas={f.canvas} area={f.area} label={`${f.headline} route`} />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 1.5, borderTop: "1px solid", borderColor: "divider", fontSize: "0.9rem" }}>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>Service area</Typography>
                  <Typography sx={{ color: "dark.main", fontWeight: 700, fontSize: "0.9rem" }}>{f.area}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, pt: 1, borderTop: "1px solid", borderColor: "divider", fontSize: "0.9rem" }}>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>Fleet</Typography>
                  <Typography sx={{ color: "dark.main", fontWeight: 700, fontSize: "0.9rem" }}>{f.fleet}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} sx={{ p: { xs: 3, md: 4.5 }, display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.84rem", color: ACCENT_TEXT }}>{f.kicker}</Typography>
                <Typography variant="h3" sx={{ mt: 1, fontWeight: 800, fontSize: { xs: "1.4rem", md: "1.7rem" }, color: "dark.main", lineHeight: 1.15 }}>{f.headline}</Typography>
                <Typography sx={{ mt: 1.75, color: "text.secondary", fontSize: "1rem", lineHeight: 1.6 }}>{f.body}</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 3 }}>
                  {f.chips.map((c) => (
                    <Chip key={c} label={c} size="small" sx={{ borderRadius: 2, backgroundColor: "primary.extraLight", color: "primary.extraDark", fontWeight: 600, fontSize: "0.78rem" }} />
                  ))}
                </Box>
                <Box sx={{ display: "flex", gap: 4, mt: 3.5, pt: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
                  {f.stats.map((s) => (
                    <Box key={s.l}>
                      <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "dark.main", lineHeight: 1.1 }}>{s.n}</Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>{s.l}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mt: "auto", pt: 3.5 }}>
                  <Button
                    component={Link}
                    to={`/examples/${f.exampleSlug}`}
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "industry_flagship_open", page: `industries/${slug}` })}
                    sx={{
                      backgroundColor: "primary.main",
                      px: 3,
                      py: 1.1,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "primary.extraDark" },
                    }}
                  >
                    Open this example
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Box>

        {/* Supporting examples */}
        <Box sx={{ py: { xs: 4, md: 6 } }}>
          <Box sx={{ mb: 4 }}>
            <Eyebrow>{industry.supportingHead.eyebrow}</Eyebrow>
            <Typography variant="h2" sx={{ mt: 1.5, fontWeight: 800, fontSize: { xs: "1.6rem", md: "2.1rem" }, color: "dark.main" }}>{industry.supportingHead.h2}</Typography>
          </Box>
          <Grid container spacing={2}>
            {industry.supporting.map((s) => (
              <Grid item xs={12} md={4} key={s.title}>
                <Card variant="outlined" component={Link} to={s.exampleSlug ? `/examples/${s.exampleSlug}` : "/use-cases"}
                  sx={{ height: "100%", borderRadius: 3, p: 2.75, display: "flex", flexDirection: "column", textDecoration: "none", transition: "all .18s", "&:hover": { borderColor: "primary.main", transform: "translateY(-2px)" } }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: ACCENT_TEXT, mb: 0.5 }}>{s.city}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "dark.main", mb: 0.75 }}>{s.title}</Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.93rem", lineHeight: 1.5 }}>{s.body}</Typography>
                  <Typography sx={{ mt: "auto", pt: 1.5, fontSize: "0.88rem", fontWeight: 600, color: "text.secondary" }}>{s.foot} →</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Final CTA */}
        <Box sx={{ textAlign: "center", py: { xs: 6, md: 9 } }}>
          <Eyebrow center>Ready when you are</Eyebrow>
          <Typography variant="h2" sx={{ mt: 2, fontWeight: 800, fontSize: { xs: "1.7rem", md: "2.2rem" }, color: "dark.main", maxWidth: 560, mx: "auto" }}>{industry.final.h2}</Typography>
          <Typography sx={{ mt: 2, color: "text.secondary", fontSize: "1.1rem", lineHeight: 1.55, maxWidth: 520, mx: "auto" }}>{industry.final.body}</Typography>
          <Box sx={{ mt: 3.5, display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              component={Link}
              to="/model-builder"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "industry_final_model_builder", page: `industries/${slug}` })}
              sx={{
                backgroundColor: "primary.main",
                px: 3.5,
                py: 1.35,
                fontSize: "1rem",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "primary.extraDark" },
              }}
            >
              Plan with your own fleet
            </Button>
            {/* The industry-filtered gallery. The hero and the flagship card
                both open the flagship example already; a third link to it would
                leave no way into the rest of this industry's examples. */}
            <Button
              component={Link}
              to={`/use-cases${industry.useCasesQuery || ""}`}
              variant="outlined"
              onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "industry_final_use_cases", page: `industries/${slug}` })}
              sx={{
                px: 3.5,
                py: 1.35,
                fontSize: "1rem",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "divider",
                color: "dark.main",
                "&:hover": { borderColor: "primary.main", color: "primary.main" },
              }}
            >
              Browse the examples
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
